// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import axios from 'axios';
import React from 'react';
import { toast } from 'react-toastify';
import Scrollbars from 'react-custom-scrollbars';
import Foco from 'react-foco';

import api from '../../../api';

// Components
import Page from './Page';
import TableEditor from './TableEditor';
import rotateTable from '../../../utilities/rotateTable';
import ContextMenu from '../../ContextMenu';

// Utilities
import copyText from '../../../utilities/copy';

// Constants
import { RENDER_COUNT, CHUNK_SIZE, STATIC_LOAD_MAX, ASPECT_RATIO, PAGE_MARGIN } from './constants';
import MatchCounter from '../../MatchCounter';
const WORDS_DAT_SIZE = 42;

const colors = [
    "rgb(6,147,227)", // Blue
    "rgb(0, 208, 132)", // Green
    "rgb(235,20,76)", // Red
    "rgb(40,40,40)", // Black
];

export default class Document extends React.Component {

    state = {

        // Blob paths for downloaded pages
        pages: {},
        table: {},
        canvas: {},
        changed: [],

        // Selection
        selection: [],

        // Toolbar
        zoom: 1,
        showToolbar: false,
        colorPicker: false,
        toolColor: colors[3],
        activeTool: "",
        saveStatus: "",
        newComment: null,

        // Table editor
        contextMenuCell: {},

        // Dynamic loading
        dynamicLoad: false,
        startIdx: 0,
        endIdx: RENDER_COUNT-1,
        aspects: [],
        showPageCounter: false,
        currentPage: 0,

        // Control key down?
        ctrlDown: false,
        shiftDown: false, 
    }

    // API URL
    url = '/document'

    // Share a cancelation token across pages for easy unmounting
    cancelToken = axios.CancelToken.source();

    jobUpdateInterval = null

    constructor(props) {
        super(props)

        this.keydownEvent = ((event) => {
            if (event.key === "Control" || event.key === "Alt")
                this.setState({ ctrlDown: true });

            if (event.key === "Shift")
                this.setState({ shiftDown: true }); 

            if (event.key === "c" && event.ctrlKey)
                this.copy();

            if (event.key === "Escape"){
                this.setState({ activeTool: "" })
                this.props.setFunction(); 
            }

            if (event.ctrlKey && (event.key === "ArrowUp" || event.key === "=")){
                this.docZoomHandler(this.state.zoom * 1.25)
            }

            if (event.ctrlKey && (event.key === "ArrowDown" || event.key === "-")){
                this.docZoomHandler(this.state.zoom / 1.25)
            }
        })
    }

    componentDidMount = async () => {
        
        this.docZoomHandler((this.refs.document.clientWidth - 100) / (0.78 * this.refs.document.clientHeight - 60))

        await this.loadDocument(this.props.match.params.documentId);

        if (this.props.match.params.query && this.props.match.params.query.length > 2)
            this.props.search(this.props.match.params.query.replace(/\|/g, '/'));

        // Setup keyboard shortcuts

        window.addEventListener("keydown", this.keydownEvent);
        window.addEventListener("mousedown", this.handleMouseDown); 

        window.addEventListener("keyup", (event) => {
            if (event.key === "Control" || event.key === "Alt")
                this.setState({ ctrlDown: false });

            if (event.key === "Shift") 
                this.setState({ shiftDown: false });
        });

        // Refs
        this.centerPage = React.createRef();

        // From tag? Jump to comment
        const query = new URLSearchParams(this.props.location.search);
        const comment = query.get('comment');
        if (comment)
            setTimeout(() => {
                const commentDiv = document.getElementById('comment-' + comment);
                if (commentDiv) commentDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 500);

        // If page number is set, jump to page
        const page = query.get('page');
        if (page)
            setTimeout(() => {
                const pageDiv = document.getElementById('page-' + String(page));
                if (pageDiv) pageDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 500);

        window.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillReceiveProps = async (next) => {
        // Reload (correct) document if updated
        if (next.match.params.documentId !== this.props.match.params.documentId)
            await this.loadDocument(next.match.params.documentId);

        if (next.match.params.query !== this.props.match.params.query && next.match.params.query.length > 2)
            this.props.search(next.match.params.query);
        
        // has the selected table changed? 
        if (next.editTableIdx !== this.props.editTableIdx && next.editTableIdx >= 0){
            let table = this.props.document.tables[next.editTableIdx];
            if (next.editTableIdx >= 0 && table) {
                table.rows = table.rows.map(row => ({
                    ...row,
                    page: row.page ?? table.page
                }));
                table = rotateTable(table, this.props.document.rotations, true);
            }
            // Handle sorting the rows
                    // Fpr each page, sort the rows by y position
           

            table.rows = this.sortTableRows(table.rows); 
            this.setState({ table: next.editTableIdx >= 0 ? table : {}});
        }
        // Have table tags been updated? 
        if ((next.editTableIdx === this.props.editTableIdx) && this.props.editTableIdx >= 0){

            // Get the current table, and compare that to next.table 
            const currentTable = this.state.table; 
            const nextTable = next.document.tables[next.editTableIdx]; 

            // Compare the tags of both tables 
            if (JSON.stringify(currentTable?.tags) !== JSON.stringify(nextTable?.tags)){
                this.setState({table: {...currentTable, tags: nextTable.tags}});
            }
        }

        // Are we wanting to splice the docoument? adjust the zoom... 
        if(next.function === 'splice-document' && this.state.zoom > 1)
            this.docZoomHandler(1); 

    }

    componentWillUnmount = async () => {
        this.unloadPageData();
        this.cancelToken.cancel("Document unmount");

        window.removeEventListener('keydown', this.handleKeyDown);

        window.removeEventListener("keydown", this.keydownEvent, false);

        window.removeEventListener("mousedown", this.handleMouseDown); 

        if (this.jobUpdateInterval)
            clearInterval(this.jobUpdateInterval);

        if (this.props.function) 
            this.props.setFunction();  

        clearTimeout(this.scrollTimer);
    }

    handleMouseDown = (event) => {
        if(this.props.function !== 'splice-document') return; 
        // Confirm it was a left click 
        if(event.button !== 0) return; 

        // Parse the page the user is selecting... 
        const page = parseInt(event.target.id.split("-").pop()); 
        if(isNaN(page)) return; 
        
        let selectedPages = this.props.functionData.pages??[];  
        let closestSelectedPage = selectedPages.reduce((prev, curr) => Math.abs(curr - page) < Math.abs(prev - page) ? curr : prev, -1);  
        if (closestSelectedPage < 0)
            closestSelectedPage = 0; 

        // If page is already selected, remove it from the selection  
        if(selectedPages.includes(page))
            selectedPages = selectedPages.filter((p) => p !== page);  
        else{
            selectedPages.push(page); 

            // If the user is holding shift, and they clicked on a new page, select all pages between the closest selected page and the clicked page. 
            if(this.state.shiftDown){
                const range = Array(Math.abs(closestSelectedPage - page)).fill(0).map((_, idx) => Math.min(closestSelectedPage, page) + idx); 
                selectedPages = [...selectedPages, ...range];   

                // Remove any duplicates from the selected pages array 
                selectedPages = [...new Set(selectedPages)]; 
            }
        }
            
        // Update the selected pages
        this.props.setFunction('splice-document', {
            ...this.props.functionData,
            pages: selectedPages
        });
    }

    getPageBounds = (pageNumber) => {
        const page = document.getElementById(`page-${pageNumber}`);
        const bounds = {
            left: page?.offsetLeft,
            top: page?.offsetTop,
            width: page?.offsetWidth,
            height: page?.offsetHeight,
        }
        return bounds;
    }

    getCellPosition = (row, cell) => {
        const pageBounds = this.getPageBounds(row.page);

        const left = (cell.x * pageBounds.width) + pageBounds.left;
        const top = (cell.y * pageBounds.height) + pageBounds.top;
        const width = cell.width * pageBounds.width;
        const height = cell.height * pageBounds.height;

        return {left, top, width, height};
    }
    
    normalizeBounds = (cellPosition, page) => {
        const pageBounds = this.getPageBounds(page);

        const x = (cellPosition.left - pageBounds.left) / pageBounds.width;
        const y = (cellPosition.top - pageBounds.top) / pageBounds.height;
        const width = cellPosition.width / pageBounds.width;
        const height = cellPosition.height / pageBounds.height;
    
        return {...cellPosition,  x, y, width, height };
    }

    getTableRotation = (row) => {
        if(!row || !row.cells || !row.cells.length) return 'rotated-0';
        
        const {left: leftOne, top: topOne, width: widthOne, height: heightOne} = 
            this.getCellPosition(row, row.cells[0]);
        const {left: leftTwo, top: topTwo} =
            this.getCellPosition(row, row.cells[row.cells.length - 1]); 
        
        let orientation = ''; 
        if(leftTwo > (leftOne + (widthOne * .8)))
            orientation = 'rotated-0';
        else if (leftTwo < (leftOne - (widthOne * .8)))
            orientation = 'rotated-180';
        else if (topTwo > (topOne + (heightOne * .8)))
            orientation = 'rotated-90';
        else if (topTwo < (topOne - (heightOne * .8)))
            orientation = 'rotated-270';

        return orientation;
    }

    sortTableRows = (rows) => {
        const pages = Array.from({ length: this.props.document.pages.length }, () => []);
        for(const row of rows){
            if(!pages[row.page]) pages[row.page] = [];
            pages[row.page].push(row);
        }

        // For each page, sort the rows based on y position... 
        pages.forEach((page, idx) => {
            if(page.length === 0) return page; 
            const orientation = this.getTableRotation(page[0]);

            // sort the rows based on x position... (Create groups of side by side rows)
            page.sort((a, b) => 
                orientation === 'rotated-0'? a.x - b.x:
                orientation === 'rotated-90'? a.y - b.y:
                orientation === 'rotated-180'? b.x - a.x:
                orientation === 'rotated-270'? b.y - a.y: 0
            )

            const rowGroups = []; 
            let currentGroup = []; 

            page.forEach((row, idx) => {
                if(currentGroup.length === 0){
                    currentGroup.push(row); 
                    return; 
                }
                // get the row to compare against... 
                const compare = currentGroup[0];
                const inSameGroup = () => {
                    return orientation === 'rotated-0'? row.x < compare.x + compare.width: 
                    orientation === 'rotated-180'? row.x > compare.x + compare.width:
                    orientation === 'rotated-90'? row.y < compare.y + compare.height: 
                    orientation === 'rotated-270'? row.y > compare.y + compare.height: false
                }

                if(inSameGroup()){
                    currentGroup.push(row);
                } else {
                    rowGroups.push([...currentGroup]);
                    currentGroup = [row];
                }
            });

            // Add the last group to the rowGroups array
            rowGroups.push([...currentGroup]);

            // Sort each group by y position:
            rowGroups.forEach((group, idx) => {
                group.sort((a, b) =>
                    orientation === 'rotated-0'? a.y - b.y:
                    orientation === 'rotated-90'? b.x - a.x:
                    orientation === 'rotated-180'? b.y - a.y:
                    orientation === 'rotated-270'? a.x - b.x: 0
                );
            });


            const rows = rowGroups.flat(); 
            rows.forEach((row, idx) => {
                row.cells.sort((a, b) =>  
                    orientation === 'rotated-0'? a.x - b.x:
                    orientation === 'rotated-90'? a.y - b.y:
                    orientation === 'rotated-180'? b.x - a.x:
                    orientation === 'rotated-270'? b.y - a.y: 0
                );
            });

            // Flatten the groups...
            pages[idx] = rows;
        }); 
        return pages.flat(); 
    }

    getAccessHandler = () => this.props.accessHandler ? this.props.accessHandler : async call => await call();
    getAccessAuth = () => this.props.auth ? this.props.auth : "";

    // Handle dynamic loading of pages through scrolling
    scrollTimer = -1;
    onScrollEnd = () => {

        const docLen = this.props.document.pages.length;
        const { page } = this.getCurrentPage();

        // Show relevant pages
        const endIdx = Math.min(docLen-1, Math.ceil(page + RENDER_COUNT));
        const startIdx = Math.max(0, Math.floor(page - RENDER_COUNT));

        // Start loading chunk of pages centered around scroll view
        const chunkStart = Math.min(startIdx, Math.round(page - CHUNK_SIZE/2));

        this.loadPages(chunkStart, CHUNK_SIZE);

        this.setState({
            startIdx,
            endIdx,
            currentPage: page
        });
    }

    getCurrentPage = () => {
        // Get scroll data
        const sHeight = this.refs.documentViewerScroll.getScrollHeight();
        const sPos =  this.refs.documentViewerScroll.getScrollTop();

        // Find the page that crosses the middle of the screen
        const windowPos = sPos + window.innerHeight/2;

        // Determine mean page height
        const docLen = this.props.document.pages.length;

        if (typeof windowPos !== "number" || typeof sHeight !== "number" || 
            typeof docLen !== "number")
            return;

        // Init page with the assumption that all pages are the same height
        let page = Math.floor((sPos/sHeight) * docLen + 0.5);
        let scrollOffset = 0;

        // If we have aspect ratios, find page height
        const doc = this.props.document;
        if (doc.aspects && Object.keys(doc.aspects).length === docLen) {
            const pWidth = this.state.zoom * (window.innerHeight * 0.78 - 60);
            let currHeight = 0;
            for (let pageIdx=0; pageIdx<doc.pages.length-1; pageIdx++) {

                // Adjust aspect ratio if rotated
                let AR = doc.aspects[String(pageIdx)];
                if (doc.rotations && [90, -90, 270].includes(doc.rotations[pageIdx]))
                    AR = 1/AR;

                const pageHeight = (pWidth * AR + PAGE_MARGIN);

                // Once current height is > middle of screen we have passed our page
                if (currHeight + pageHeight >= windowPos) {
                    scrollOffset =  sPos - currHeight;
                    page = pageIdx;
                    break;
                }

                currHeight += pageHeight;
            }
        }

        return { page, scrollOffset };
    }

    scrollToPage = (page, scrollOffset=0) => {
        // If we have aspect ratios, find page heights
        const doc = this.props.document;
        if (doc.aspects && Object.keys(doc.aspects).length === doc.pages.length) {

            const pWidth = this.state.zoom * (window.innerHeight * 0.78 - 60);
            let scrollTo = 0;
            for (let pageIdx=0; pageIdx<doc.pages.length; pageIdx++) {

                if (pageIdx === page) {
                    this.refs.documentViewerScroll.scrollTop(scrollTo + scrollOffset);
                    break;
                }
                
                // adjust aspect if rotated
                let AR = doc.aspects[String(pageIdx)];
                if (doc.rotations && [90, -90, 270].includes(doc.rotations[pageIdx]))
                    AR = 1/AR;

                scrollTo += (pWidth * AR + PAGE_MARGIN);
            }
        }

        // Assume page heights are all the same
        else {
            const sHeight = this.refs.documentViewerScroll.getScrollHeight();
            const scrollTo = sHeight * (page/doc.pages.length);
            this.refs.documentViewerScroll.scrollTop(scrollTo + scrollOffset);
        }
    }

    onDocScroll = async (e) => {
        
        if (this.pageCounterTimer)
            clearTimeout(this.pageCounterTimer);


        const { page } = this.getCurrentPage();
        this.setState({
            showPageCounter: true,
            currentPage: page // track page idx and just +1 in render function
        });

        this.pageCounterTimer = setTimeout(() => this.setState({ showPageCounter: false }), 2000);

        // Dynamically load/render pages if needed
        if (this.state.dynamicLoad) {
            if (this.scrollTimer)
                clearTimeout(this.scrollTimer);
            this.scrollTimer = setTimeout(this.onScrollEnd, 500);
        }
    }

    // A wrapper to load our page data in chunks
    loadPages = async (startIdx, length, auth=null) => {
        // Load pages in chunks
        let doc = this.props.document;

        // For each page
        let promises = [];
        for (let i=startIdx; i<startIdx + length; i++) {

            // Dont reload pages
            if (i < 0 || i >= doc.pages.length || 
                typeof this.state.pages[i] === "string")
                continue;

            promises.push(this.loadPage(doc, doc.pages[i], i, auth));
        }

        // Wait for this chunk before proceeding
        await Promise.all(promises);
        promises = [];
    }

    loadPage = async (doc, pageKey, pageIdx, auth=null) => {

        if (!auth)
            auth = this.getAccessAuth();

        // Dont reload pages!
        if (this.state.pages[pageIdx] !== undefined)
            return;

        // Apply rotations
        // eslint-disable-next-line
        if (doc.rotations && doc.rotations[pageIdx] != 0) {
            const tmp = setInterval(() => {
                // Check for words loading completion
                if (!this.state.loadedWords) return;
                this.props.rotatePage(pageIdx, doc.rotations[pageIdx], false, true);
                clearInterval(tmp);
            }, 500);
        }

        let data = await this.getAccessHandler()(async () => await api.download(pageKey, false, {cancelToken: this.cancelToken.token}, true));

        if (!data)
            return;

        await this.setState({
            pages: {
                ...this.state.pages,
                [pageIdx]: window.URL.createObjectURL(new Blob([data], { type: 'image/jpeg' }))
            }
        });
    }

    loadDocumentMeta = async (documentId, auth=null) => {

        if (!auth)
            auth = this.getAccessAuth();

        if (this.jobUpdateInterval)
            clearInterval(this.jobUpdateInterval);

        const res = await this.getAccessHandler()(async () => await api.get(this.url + '/get?docId=' + documentId + '&presigned=true&statement=true' + auth, {}, false, {cancelToken: this.cancelToken.token}));

        if (!res || !res.doc) return;

        let doc = { ...res.doc };
        const statement = {...res.statement??{}};

        if (!doc.viewed || !doc.viewed[String(this.props.user._id)])
            await this.props.viewDoc(doc);

        await this.props.setDocument({
            ...doc,
            jobs: this.props.document?.jobs ?? [],
            duplicates: this.props.document?.duplicates ?? [],
        });

        // Core document meta has been loaded. Let's load the remaining in parallel so we can get started on loading pages already!
        (async () => {
            // Init all our aspects using default
            let aspects = [];
            for (let pageIdx=0; pageIdx<doc.pages.length; pageIdx++)
                aspects.push(ASPECT_RATIO);

            // Now add in aspects from props if available
            if (doc.aspects) {
                for (const [k,v] of Object.entries(doc.aspects)) {
                    let key = Number(k);
                    if (!isNaN(key)) {
                        aspects[key] = v;
                    }
                }
            };

            await this.setState({ aspects, loadedWords: false });

            if (doc.wordsUrl) {
                const res5 = await api.download(doc.wordsUrl, false, {}, true);
                const buffer = new Buffer(await res5.arrayBuffer());
                const words = [];
                let cursor = 0;

                while (cursor+WORDS_DAT_SIZE < buffer.length) {
                    // Word meta
                    const meta = buffer.slice(cursor,cursor+WORDS_DAT_SIZE);
                    const size = meta.readUInt16LE(WORDS_DAT_SIZE-2);
                    const text = buffer.slice(cursor+WORDS_DAT_SIZE, cursor+WORDS_DAT_SIZE+size);
        
                    words.push({
                        x: meta.readFloatLE(0),
                        y: meta.readFloatLE(4),
                        width: meta.readFloatLE(8),
                        height: meta.readFloatLE(12),
                        angle: meta.readFloatLE(16),
                        page: meta.readUInt16LE(28),
                        group: meta.readUInt16LE(30),
                        line: meta.readUInt16LE(32),
                        word: meta.readUInt16LE(34),
                        class: meta.readUInt16LE(36),
                        text: text.toString()
                    });
        
                    // Keep moving
                    cursor += WORDS_DAT_SIZE + size;
                }
                this.props.setWords(doc._id, words);
            }

            this.setState({ loadedWords: true });
            const f = (key) => doc.metadata.find(f => f.key === key)?.value;

            if(statement && statement?._id){
                this.props.setStatement({
                    forDocument: doc._id, 
                    ...statement
                })

                this.setState({ 
                    statementConfirmed: statement?.confirmed || false,
                    statementClientConfirmed: statement?.clientConfirmed || false
                });
            }



            // If this is a statement (has a plan id, account number, and period end date, load the associated statement)
            if ((!f('plan id') || !f('account') || !f('period end')) || statement?._id)
                return;

            this.props.setStatement({ forDocument: doc._id });

            const res6 = await api.post(`/tpa/statement/find`, {
                docId: doc._id,
                planId: f('plan id'),
                accountNumber: f('account'),
                periodEnd: f('period end'),
                periodStart: f('period start')
            });

            if (!res6 || !res6.statement) return;

            this.setState({ statementConfirmed: res6.statement?.confirmed || false,
                            statementClientConfirmed:  res6.statement?.clientConfirmed || false}); 

            this.props.setStatement({
                ...res6.statement,
                forDocument: doc._id
            });
        })();
    }

    unloadPageData = async () => {
        Object.values(this.state.pages).map(url => window.URL.revokeObjectURL(url));
        await this.setState({ pages: {} });
    }

    loadDocument = async (documentId, auth=null) => {

        let doc = this.props.document;
        
        // Load from API?
        if (!doc || doc._id !== documentId || !doc.pages || !doc.words || !doc.metadata || !doc.rotations) {
            await this.loadDocumentMeta(documentId, auth);
            doc = this.props.document; // Reload
        }

        // else if (!doc.words || !doc.metadata || !doc.rotations)
        //     setTimeout(() => this.loadDocumentMeta(documentId), 1); // We can do this in the background as pages load... :)

        if (!doc.pages || doc.pages.length === 0)
            return;

        // Revoke old document pages
        await this.unloadPageData();

        // Check if doc is too big for static loading
        if (doc.pages.length > STATIC_LOAD_MAX && doc.aspects && Object.keys(doc.aspects).length === doc.pages.length) {
            // Download first chunk asynchronously 
            this.loadPages(0, CHUNK_SIZE, auth);
            this.setState({
                dynamicLoad: true,
            });
        } else {
            this.loadPages(0, doc.pages.length, auth);
            this.setState({
                endIdx: doc.pages.length-1
            });
        }
        
    }

    saveCanvas = async (pageIdx) => {
        await this.setState({
            saveStatus: "Pending changes...",
            changed: !this.state.changed.includes(pageIdx) ? [ ...this.state.changed, pageIdx ] : this.state.changed
        });
        if (this.saveTimeout) clearTimeout(this.saveTimeout);

        this.saveTimeout = setTimeout(async () => {
            // const canvas = { ...this.props.document.canvas };
            await this.setState({ saveStatus: "Saving..." });
            // Add updated pages to canvas
            for (let page of this.state.changed)
                if (this.state.canvas[page]) {
                    await api.post(this.url + '/save', {
                        docId: this.props.document._id,
                        pageIdx: page,
                        canvas: this.state.canvas[page].toDataURL('image/png')
                    }, true);
                }
            
            await this.setState({
                saveStatus: "Saved.",
                changed: []
            });

            setTimeout(() => this.setState({ saveStatus: "" }), 1000);
        }, 2000);
    }
    
    backToStack = () => {
        this.props.search("");
        let query = this.props.match.params.query;
        const { field, value } = this.props.match.params;

        let path = window.location.href.includes('/field/') && field && value ? '/field/' + field + '/' + value : '/stack/' + this.props.match.params.stackId;

        const params = new URLSearchParams(this.props.location.search);
        const from = params.get('from');

        if (from === "root")
            path = "";

        else if (from === "field")
            path = '/field/' + params.get('field') + '/' + params.get('value');

        else if (from === "archives")
            return window.location.href = "/archives";
        
        else if (from)
            path = '/stack/' + from;

        if (query && query.length > 2) {
            if (query[0] === '*')
                query = query.substring(1);

            path += "/search/" + query + this.props.location.search;
        }

        this.props.history.push(path);
    }

    showToolbar = () => {
        if (this.hideToolbarTimeout)
            clearTimeout(this.hideToolbarTimeout);

        this.setState({ showToolbar: true });
        this.hideToolbarTimeout = setTimeout(() => this.setState({
            showToolbar: false,
            colorPicker: false
        }), 3000);
    }

    setCanvasCursor = (cursor) => {
        for (const canvas of Object.values(this.state.canvas))
                canvas.style.cursor = cursor;
    }

    docZoomHandler = async (zoom) => {

        // scroll top
        let { page, scrollOffset } = this.getCurrentPage();
        const zoomFactor = this.state.zoom/zoom;

        await this.setState({ zoom }, () => {
            this.scrollToPage(page, zoomFactor*scrollOffset);
            this.forceUpdate();
        });
    }

    tools = [
        {
            name: "Fit to screen",
            icon: "fit-screen.png",
            action: () => this.docZoomHandler(1)
        },
        {
            name: "Fit page width",
            icon: "fit-width.png",
            action: () => this.docZoomHandler((this.refs.document.clientWidth - 100) / (0.78 * this.refs.document.clientHeight - 60))
        },
        {
            name: "Zoom in",
            icon: "zoom-in.png",
            action: () => this.docZoomHandler(this.state.zoom * 1.25 )
        },
        {
            name: "Zoom out",
            icon: "zoom-out.png",
            action: () => this.docZoomHandler(this.state.zoom / 1.25)
        },
        {
            name: "Select",
            icon: "select.png",
            action: () => {
                this.setState({ activeTool: "" });
                this.setCanvasCursor("auto");
            }
        },
        {
            name: "Pick color",
            icon: "color.png",
            action: () => this.setState({ colorPicker: !this.state.colorPicker })
        },
        {
            name: "Pencil",
            icon: "pencil.png",
            action: () => {
                this.setState({ activeTool: "Pencil" });
                this.setCanvasCursor("url('https://static.stax.ai/images/tools/pencil-cursor.png') 0 32, auto");
            }
        },
        {
            name: "Eraser",
            icon: "eraser.png",
            action: () => {
                this.setState({ activeTool: "Eraser" });
                this.setCanvasCursor("url('https://static.stax.ai/images/tools/eraser-cursor.png') 12 32, auto");
            }
        },
        {
            name: "InsertBox",
            icon: "box.png",
            action: () => {
                this.setState({ activeTool: "InsertBox" });
                this.setCanvasCursor("crosshair");
            }
        },
        {
            name: "Textbox",
            icon: "text.png",
            action: () => {
                this.setState({ activeTool: "Textbox" });
                this.setCanvasCursor("text");
            }
        },
        {
            name: "Sign",
            icon: "signature.png",
            action: () => {
                if (!this.props.user.signature || this.props.user.signature === "")
                    return toast.info(<>No signature set. Go to your <a href="/account">account settings page</a> to create a signature.</>);

                this.setState({ activeTool: "Sign" });
                this.setCanvasCursor("url('https://static.stax.ai/images/tools/signature-pen.png') 0 32, auto");
            }
        }
    ]

    renderToolbar = () => this.tools.map((tool,idx) => <React.Fragment key={idx}>
        <div
            key={idx}
            className={"document-toolbar-tool" + (this.state.activeTool === tool.name ? " document-toolbar-tool-active" : "")}
            style={{
                backgroundImage: "url('https://static.stax.ai/images/tools/" + tool.icon + "')"
            }}
            title={tool.name}
            onClick={tool.action}
        />
        {idx < this.tools.length && <div className="document-toolbar-tool-separator" />}
    </React.Fragment>);

    copy = () => {
        if (this.state.selection.length === 0) return;
        // Compile selection with newline characters
        const sel = this.state.selection;
        let text = "";
        for (const [idx, word] of sel.entries()) {
            text += word.text;
            text += (idx < sel.length-1 && !sel[idx+1].word) ? "\n" : " ";
        }

        copyText(text);        
        toast.info("Copied to clipboard");
    }

    handleKeyDown = (event) => {
        if (event.ctrlKey && event.key === 'd') {
            event.preventDefault();
            this.performDeleteAction(true);

        } else if (event.ctrlKey && event.key === 'a' && this.props.editTableIdx >= 0) {
            event.preventDefault();
            this.props.clearSelection(document?._id);
            this.addRowBottom(true);
        
        } else if (event.ctrlKey && event.key === 'p') {
            event.preventDefault();
            this.populateEmptyCells();

        }
        else if (event.ctrlKey && event.key === 'r') {
            event.preventDefault();
            this.dragSelectRow(); 
        }else if (this.state.ctrlDown && event.key === 'a' && this.props.function === 'splice-document') {
            event.preventDefault();  
            this.props.setFunction("splice-document", {pages: Array.from({length: this.props.document.pages.length}, (_, idx) => idx)});     
        }
    }

    fillCellValue = (cell, row) => {
        //Helper function to return string of words within the cell bounds 
        let result = [];
        this.props.document.words.filter((word) => word.page === row.page).forEach(word => {
            // check if the word is within the bounds of the cell 
            // find the % of overlap between the word bounds and the cell bounds 
            const overlap = Math.max(0, Math.min(cell.x + cell.width, word.x + word.width) - Math.max(cell.x, word.x)) * Math.max(0, Math.min(cell.y + cell.height, word.y + word.height) - Math.max(cell.y, word.y)) / (word.width * word.height);

            if(overlap > 0.7)
                result.push(word);
        });

        const lines = {};
        // filter the result so each line is a separate array
        result.forEach((word) => {
            if(!lines[word.line])
                lines[word.line] = [];
            lines[word.line].push(word);
            // sort the words by x position
            lines[word.line].sort((a, b) => a.x - b.x);
        });

        // add the words to the result string in order of line 
        Object.keys(lines).sort(); 
        result = Object.keys(lines).map((line) => {
            return (lines[line].map((word) => word.text).join(' '))+ '\n';
        }).join('');

        return result;
    }

    populateEmptyCells = () => {
        const table = this.state.table;
        table.rows.forEach((row) => {
            row.cells.forEach((cell) => {
                if(cell.text)
                    return; 
                cell.text = this.fillCellValue(cell, row);
            })
        })
        this.setState({table, contextMenuCell: {}});
        this.props.saveTableChanges(table);
    }



    splitCellRow = (rowIdx, bottom=false) => {
        const table = {...this.state.table}; 
        const row = table.rows[rowIdx] ?? table.rows?.filter(r => r.page === this.state.currentPage)?.slice(-1)[0];

        // Get the table orientation... 
        const orientation = this.getTableRotation(row);
        let newRow; 
        switch (orientation){
            case 'rotated-0':
                newRow = {
                    x: row.x,
                    y: row.y + row.height / 2,
                    width: row.width,
                    height: row.height / 2,
                    page: row.page,
                    cells: new Array(row.cells.length).fill(0).map((_, idx) => {
                        return {
                            x: row.cells[idx].x,
                            y: row.y + row.height / 2,
                            width: row.cells[idx].width,
                            height: row.cells[idx].height / 2,
                            text: bottom? row.cells[idx].text : '',
                        }
                    })
                }
                row.height /= 2;
                row.cells.forEach((cell) => {
                    cell.height /= 2;
                    cell.text = bottom? '' : cell.text;
                });
                break; 
            case 'rotated-90':
                newRow = {
                    x: row.x + row.width / 2,
                    y: row.y,
                    width: row.width / 2,
                    height: row.height,
                    page: row.page,
                    cells: new Array(row.cells.length).fill(0).map((_, idx) => {
                        return {
                            x: row.cells[idx].x + row.width / 2,
                            y: row.cells[idx].y,
                            width: row.cells[idx].width / 2,
                            height: row.cells[idx].height,
                            text: bottom? row.cells[idx].text : '',
                        }
                    })
                }
                row.width /= 2;
                row.cells.forEach((cell) => {
                    cell.width /= 2;
                    cell.text = bottom? '' : cell.text;
                });
                break; 
            case 'rotated-180':
                newRow = {
                    x: row.x,
                    y: row.y + row.height / 2,
                    width: row.width,
                    height: row.height / 2,
                    page: row.page,
                    cells: new Array(row.cells.length).fill(0).map((_, idx) => {
                        return {
                            x: row.cells[idx].x,
                            y: row.y + row.height / 2,
                            width: row.cells[idx].width,
                            height: row.cells[idx].height / 2,
                            text: bottom? row.cells[idx].text : '',
                        }
                    })
                }
                row.height /= 2;
                row.cells.forEach((cell) => {
                    cell.height /= 2;
                    cell.text = bottom? '' : cell.text;
                });
                break;
            case 'rotated-270':
                newRow = {
                    x: row.x + row.width / 2,
                    y: row.y,
                    width: row.width / 2,
                    height: row.height,
                    page: row.page,
                    cells: new Array(row.cells.length).fill(0).map((_, idx) => {
                        return {
                            x: row.cells[idx].x + row.width / 2,
                            y: row.cells[idx].y,
                            width: row.cells[idx].width / 2,
                            height: row.cells[idx].height,
                            text: bottom? row.cells[idx].text : '',
                        }
                    })
                }
                row.width /= 2;
                row.cells.forEach((cell) => {
                    cell.width /= 2;
                    cell.text = bottom? '' : cell.text;
                });
                break; 
            default:
                break; 
        }




        table.rows.splice(rowIdx, 0, newRow);
        // sort the rows by y position
        table.rows = this.sortTableRows(table.rows);
        this.setState({table})
        this.props.saveTableChanges(table);
    }

    addRowBottom = (key=false) => {

        const addRow = () => {
            const table = {...this.state.table};
            let row;
            // Get the row we are adding a new row below... 
            if (key) {
                row = table.rows?.filter(r => r.page === this.state.currentPage)?.slice(-1)[0];
                if (!row) return;
            } else {
                row = table.rows[this.state.contextMenuCell.row];
            }
    
            // get the table orientation
            const orientation = this.getTableRotation(row);
    
            // Find the new row height... 
            const newHeight = Math.min(
                    orientation === 'rotated-0' || orientation === 'rotated-180'? row.height: row.width, 
                    orientation === 'rotated-0'? 1 - (row.y + row.height):
                    orientation === 'rotated-90'? row.x:
                    orientation === 'rotated-180'? row.y:
                    orientation === 'rotated-270'? 1 - (row.x + row.width): 0
                );
            
            let newRow = {}; 
            switch (orientation){
                case 'rotated-0':
                    newRow = {
                        x: row.x,
                        y: row.y + row.height,
                        width: row.width,
                        height: newHeight,
                        page: row.page,
                        cells: new Array(row.cells.length).fill(0).map((_, idx) => {
                            return {
                                x: row.cells[idx].x,
                                y: row.y + row.height,
                                width: row.cells[idx].width,
                                height: newHeight,
                                text: '',
                            }
                        })
                    }
                    break; 
                case 'rotated-90':
                    newRow = {
                        x: row.x - newHeight,
                        y: row.y,
                        width: newHeight,
                        height: row.height,
                        page: row.page,
                        cells: new Array(row.cells.length).fill(0).map((_, idx) => {
                            return {
                                x: row.cells[idx].x - newHeight,
                                y: row.cells[idx].y,
                                width: newHeight,
                                height: row.cells[idx].height,
                                text: '',
                            }
                        })
                    }
                    break; 
                case 'rotated-180':
                    newRow = {
                        x: row.x,
                        y: row.y - newHeight,
                        width: row.width,
                        height: newHeight,
                        page: row.page,
                        cells: new Array(row.cells.length).fill(0).map((_, idx) => {
                            return {
                                x: row.cells[idx].x,
                                y: row.y - newHeight,
                                width: row.cells[idx].width,
                                height: newHeight,
                                text: '',
                            }
                        })
                    }
                    break; 
                case 'rotated-270':
                    newRow = {
                        x: row.x + row.width,
                        y: row.y,
                        width: newHeight,
                        height: row.height,
                        page: row.page,
                        cells: new Array(row.cells.length).fill(0).map((_, idx) => {
                            return {
                                x: row.cells[idx].x + row.cells[idx].width,
                                y: row.cells[idx].y,
                                width: newHeight,
                                height: row.cells[idx].height,
                                text: '',
                            }
                        })
                    }
                    break; 
                default: 
                    break; 
            }
            if (key) {
                table.rows.splice(table.rows?.map((r, idx) => {
                    if (r.page === this.state.currentPage) return idx
                    return null
                })?.filter(i => i !== null && i !== undefined)?.slice(-1)[0]+1, 0, newRow);
            } else {
                table.rows.splice(this.state.contextMenuCell.row + 1, 0, newRow);
            }
            this.setState({table})
            this.props.saveTableChanges(table);
        } 

        if((this.state.table.rows[this.state.contextMenuCell.row]?.page !== this.state.table.rows[this.state.contextMenuCell.row + 1]?.page?? -1) || key){
            addRow();
            return;
        }
        this.splitCellRow(this.state.contextMenuCell.row, true);
        this.setState({contextMenuCell: {}});
    }

    dragSelectRow = () => {
        // get the bounding box of the new row 
        this.props.setFunction('draw-input-bounds', {
            callback: (page, x, y, width, height) => {
                // fit the row x and width to the table bounds 
                const table = {...this.state.table};
                // Find the last row of this page... if there is not one, the last row of the page above
                let lastRow = table.rows[table.rows.length - 1]; // (Already sorted...)


                const below = (x < lastRow.x + lastRow.width); 

                // Is x to the left of the entire last row? 
                if (below){
                    x = lastRow.x; // Sets x the same as the row above... keep things in line
                    width = lastRow.width; // Sets the width as the row above... keeps things in line
                }


                // create the new row 
                const row = {
                    x, 
                    y,
                    width,
                    height,
                    page,
                    cells: new Array(table.rows[0].cells.length).fill(0).map((_, idx) => {
                        return below?{
                            x: table.rows[0].cells[idx].x,
                            y,
                            width: table.rows[0].cells[idx].width,
                            height,
                            text: ''
                        }: 
                        {
                            x: x + (lastRow.width / lastRow.cells.length) * idx,
                            y,
                            width: lastRow.width / lastRow.cells.length,
                            height,
                            text: ''
                        }
                    })
                }

                table.rows.push(row);
                table.rows = this.sortTableRows(table.rows); 
                this.setState({table, contextMenuCell: {}})
                this.props.saveTableChanges(table);
                this.props.setFunction();    
            }
        })
    }

    performDeleteAction = (key=false) => {
        const table = { ...this.state.table };

        if (table.rows.length <= 1) {
            // Modal confirmation before deleting the table
            this.props.modal('confirm', {
                message: (
                    <>
                        If you delete this table, you will <strong>permanently lose all table tags and populated cell values</strong>. This table will not be used when documents are looking for similar tables.
                    </>
                ),
                action: () => {
                    const tables = this.props.document.tables;
                    tables.splice(this.props.editTableIdx, 1);
                    this.props.setEditTableIdx(-1);
                    this.props.updateDocument(this.props.document._id, {tables}, true);
                }
            });
        } else {
            // Just delete the row
            let rowIdx;
            if (key) {
                rowIdx = table.rows?.map((r, idx) => {
                    if (r.page === this.state.currentPage) return idx
                    return null
                })?.filter(i => i !== null && i !== undefined)?.slice(-1)[0];
                if (!rowIdx) return;
            
            } else {
                rowIdx = this.state.contextMenuCell.row;
            }
            
            table.rows.splice(rowIdx, 1);
            this.setState({ table, contextMenuCell: {} });
            this.props.saveTableChanges(table);
        }
    }

    getNewPageRotation = (pageIdx, rotateBy) => {
        let rotation = this.props.document.rotations ? this.props.document.rotations[pageIdx]: 0;
        if (!rotation) rotation = 0;
        rotation += rotateBy;
        const turns = Math.round(rotation / 90) % 4;

        if (turns === 1)
            rotation = 90;
        else if (turns === 2)
            rotation = 180;
        else if (turns === 3)
            rotation = 270;
        else rotation = 0;

        // // Update local state aspect, keep separated from props.document to avoid over rendering
        // if (rotation !== 180 || rotation !== 0) {
        //     const aspects = {
        //         ...this.state.aspects,
        //         [pageIdx]: 1/this.state.aspects[pageIdx]
        //     }
        //     this.setState({aspects});
        // }
        
        return rotation;
    }

    getPageContextMenuOptions = () => {
       
        const doc = this.props.document;
        if (!doc) return [];
        let options = [];

        if (this.state.selection.length > 0)
            options.push(
                {
                    label: 'Copy selection',
                    action: () => this.copy()
                }, {
                    label: 'Find all matches',
                    action: () => this.props.search(this.state.selection.map(w => w.text).join(" "))
                }, {
                    label: 'Tag field',
                    action: () => {
                        const tags = this.state.selection;
                        if (tags.length === 0) return;
                        this.props.updateDocument({
                            newTag: {
                                    key: "",
                                    value: tags.map(w => w.text).join(" "),
                                    tags: tags.map(w => w.wordIdx).filter(i => i !== null && i !== undefined),
                                    pages: [ tags[0].page ]
                                }
                        });
                    }
                },
                {
                    label: 'Attach account info',
                    action: () => this.props.modal('attach-info', {selection: this.state.selection.map(s => s.text).join(' '), type: 'account'})
                }
            );

        options.push(
            {
                label: "New comment",
                action: this.addComment
            },
            {
                label: "Reprocess document",
                action: () => this.props.reprocessDocuments([doc._id])
            }, 
            {
                label: "Splice document", 
                action: () => {
                    // If this.props.function is already splice-document, then we just need to call it. 
                    if(this.props.function === "splice-document" && this.props.functionData?.pages?.length){
                        this.props.modal("splice-document", {
                            pages: this.props.functionData.pages, 
                        })
                    }else{
                        toast.info("Select pages from this document to create a new one from.");
                        this.props.setFunction("splice-document", {
                            pages: [], 
                        });
                    }
                }
            }, 
        );

        // Document navigation (next, back)
        const docIdx = this.props.docs.findIndex(d => d._id === doc._id);

        // Can we go to the next document?
        if (docIdx < this.props.docs.length-1)
            options.push({
                label: "Next document",
                action: () => {
                    this.unloadPageData();
                    this.props.history.push('/stack/' + this.props.stack._id + '/document/' + this.props.docs[docIdx+1]._id);
                }
            });

        // Can we go to the previous document?
        if (docIdx > 0)
            options.push({
                label: "Previous document",
                action: () => {
                    this.unloadPageData();
                    this.props.history.push('/stack/' + this.props.stack._id + '/document/' + this.props.docs[docIdx-1]._id);
                }
            });
        
        const rotatePage = async(page, rotation) => {
            if(!this.props.document.rotations) return; 
            const rotations = {...this.props.document.rotations};
            rotations[page] = this.getNewPageRotation(page, rotation); 
            this.props.rotatePage(page, rotations[page], true); 
            if(this.props.editTableIdx < 0)
                return;

            const table = rotateTable(this.props.document.tables[this.props.editTableIdx], rotations);
            // handle sorting the rows
            table.rows = [...this.sortTableRows(table.rows)];
            this.setState({table});
        } 

        options.push(
            {
                label: doc.urgent ? "Not urgent" : "Mark urgent",
                action: () => this.props.updateDocument({ urgent: !doc.urgent })
            },
            {
                label: doc.reviewed ? "Mark unread" : "Mark as read",
                action: () => this.props.updateDocument({ reviewed: !doc.reviewed })
            },
            {
                label: doc.archived ? "Unarchive" : "Archive",
                action: () => this.props.archiveDocument(!doc.archived)
            },
            {
                label: "View in browser",
                action: () => api.open(this.url + '/download?docId=' + doc._id)
            },
            {
                label: "Print",
                action: this.props.printDocument
            },
            {
                label: "Send",
                action: this.props.sendDocument
            },
            {
                label: "Move",
                action: this.props.moveDocument
            },
            {
                label: "Copy",
                action: this.props.copyDocumentTo
            },
            {
                label: "Rename",
                action: this.props.renameDocument
            },
            {
                label: "Delete",
                action: () => this.props.modal("confirm", {
                    message: <>You are about to delete this document. All other team users will lose access to the document as well. To restore this document, go to <a href="/trash">Trash</a>.</>,
                    action: async () => {
                        this.props.loading(9999);
                        await this.props.removeDocument();
                        this.backToStack();
                        this.props.loaded();
                    }
                })
            },
            {
                label: "Rotate page 90° ↻",
                action: async(page) => rotatePage(page, 90)
            },
            {
                label: "Rotate page 90° ↺",
                action: (page) => rotatePage(page, 270) 
            },
            {
                label: "Rotate page 180°",
                action: (page) => rotatePage(page, 180)
            },

        );

        // Admin options
        if (this.props.user?.email?.includes("@stax.ai")) {
            options.push(
                {
                    label: "Rerun pipeline",
                    action: this.props.rerunPipeline
                }
            );
        }

        // Generic options for the bottom
        options.push({
            label: "Report error",
            action: async () => this.props.modal('report-document', doc._id)
        });

        return options;
    }

    getTableContextMenuOptions = () => {

        const splitCellColumn = (columnIdx, right=false) => {
            const table = this.state.table; 
            table.rows.forEach((row) => {

                const orientation = this.getTableRotation(row); 
                const cell = row.cells[columnIdx];
                let newCell; 
                // Create a new cell which will be placed to the left of the current cell being split
                switch (orientation){
                    case 'rotated-0':
                        newCell = {
                            x: cell.x,
                            y: cell.y,
                            width: cell.width / 2,
                            height: cell.height,
                            text: right? cell.text : ''
                        }
                        // Update the current cell to be to the right of the new cell
                        cell.x += cell.width / 2; 
                        cell.width /= 2;
                        cell.text = right? '' : cell.text;
                        break;
                    case 'rotated-90': 
                        newCell = {
                            x: cell.x,
                            y: cell.y + (cell.height / 2),
                            width: cell.width,
                            height: cell.height / 2,
                            text: right? cell.text : ''
                        }
                        // Update the current cell to be to the right of the new cell
                        cell.height /= 2; 
                        cell.text = right? '' : cell.text;
                        break;
                    case 'rotated-180': 
                        newCell = {
                            x: cell.x,
                            y: cell.y,
                            width: cell.width / 2,
                            height: cell.height,
                            text: right? cell.text : ''
                        }
                        // Update the current cell to be to the right of the new cell
                        cell.x += cell.width / 2; 
                        cell.width /= 2;
                        cell.text = right? '' : cell.text;
                        break;
                    case 'rotated-270': 
                        newCell = {
                            x: cell.x,
                            y: cell.y + (cell.height / 2),
                            width: cell.width,
                            height: cell.height / 2,
                            text: right? cell.text : ''
                        }
                        // Update the current cell to be to the right of the new cell
                        cell.height /= 2; 
                        cell.text = right? '' : cell.text;
                        break;
                    default:
                        break;          
                }
                
                row.cells.splice(right? columnIdx + 1: columnIdx, 0, newCell);
            });
    
            // adjust the column headers
            const headers = table.headers;
            const header = headers[columnIdx]
            headers.splice(right? columnIdx + 1: columnIdx, 0, '');
    
            // is the new column on the right? then column idx needs the header 
            headers[columnIdx] = right? header : '';
            headers[columnIdx + 1] = right? '' : header;
            table.rows = this.sortTableRows(table.rows);
            this.setState({table})
            this.props.saveTableChanges(table);
        }
    
        const addRowTop = () => { // TODO: Hanlde rotations
            const table = {...this.state.table};

            // Get the row we are adding a new row below... 
            const row = table.rows[this.state.contextMenuCell.row];

            // get the table orientation
            const orientation = this.getTableRotation(row);

            // Find the new row height... 
            const newHeight = Math.min(
                    orientation === 'rotated-0' || orientation === 'rotated-180'? row.height: row.width, 
                    orientation === 'rotated-0'? row.y:
                    orientation === 'rotated-90'? 1 - (row.x + row.width):
                    orientation === 'rotated-180'? 1 - (row.y + row.height):
                    orientation === 'rotated-270'? row.x: 0
                );
            
            let newRow = {}; 
            switch (orientation){
                case 'rotated-0':
                    newRow = {
                        x: row.x,
                        y: row.y - newHeight,
                        width: row.width,
                        height: newHeight,
                        page: row.page,
                        cells: new Array(row.cells.length).fill(0).map((_, idx) => {
                            return {
                                x: row.cells[idx].x,
                                y: row.y - newHeight,
                                width: row.cells[idx].width,
                                height: newHeight,
                                text: '',
                            }
                        })
                    }
                    break; 
                case 'rotated-90':
                    newRow = {
                        x: row.x + row.width,
                        y: row.y,
                        width: newHeight,
                        height: row.height,
                        page: row.page,
                        cells: new Array(row.cells.length).fill(0).map((_, idx) => {
                            return {
                                x: row.cells[idx].x + row.cells[idx].width,
                                y: row.cells[idx].y,
                                width: newHeight,
                                height: row.cells[idx].height,
                                text: '',
                            }
                        })
                    }
                    break; 
                case 'rotated-180':
                    newRow = {
                        x: row.x,
                        y: row.y + row.height,
                        width: row.width,
                        height: newHeight,
                        page: row.page,
                        cells: new Array(row.cells.length).fill(0).map((_, idx) => {
                            return {
                                x: row.cells[idx].x,
                                y: row.cells[idx].y + row.cells[idx].height,
                                width: row.cells[idx].width,
                                height: newHeight,
                                text: '',
                            }
                        })
                    }
                    break; 
                case 'rotated-270':
                    newRow = {
                        x: row.x - newHeight,
                        y: row.y,
                        width: newHeight,
                        height: row.height,
                        page: row.page,
                        cells: new Array(row.cells.length).fill(0).map((_, idx) => {
                            return {
                                x: row.cells[idx].x - newHeight,
                                y: row.cells[idx].y,
                                width: newHeight,
                                height: row.cells[idx].height,
                                text: '',
                            }
                        })
                    }
                    break; 
                default: 
                    break; 
            }
            
            table.rows.splice(this.state.contextMenuCell.row, 0, newRow);
            this.setState({table})
            this.props.saveTableChanges(table);
        }
    
        return [
            {
                label: 'Delete row',
                action: () => { this.performDeleteAction(); },
                shortcut: 'Ctrl + D',
            },
            {
                label: 'Add row above',
                action: () => {
                    // Rows are sorted... if this is the first row on the page... add a full new row above, else split it... 
                    if(this.state.table.rows[this.state.contextMenuCell.row].page !== this.state.table.rows[this.state.contextMenuCell.row - 1]?.page?? -1){
                        addRowTop();
                        return;
                    }
                    this.splitCellRow(this.state.contextMenuCell.row, true);
                    this.setState({contextMenuCell: {}});
                }
            },
            {
                label: 'Add row below',
                action: () => { this.addRowBottom(); },
                shortcut: 'Ctrl + A'
            },
            {
                label: 'Drag-select new row',
                action: this.dragSelectRow,
                shortcut: 'Ctrl + R'
            },
            {
                label: 'Delete column',
                action: () => {
                    const table = {...this.state.table}
                    // Does the table have more than one row?

                    if(table.rows[0].cells.length <= 1)
                        return this.props.modal('confirm', {
                            message: <>
                                If you delete this table, you will <strong>permanently lose all table tags and populated cell values</strong>. This table will not be used when documents are looking for similar tables.
                            </>,
                            action: () => {
                                const tables = this.props.document.tables; 
                                tables.splice(this.props.editTableIdx, 1);
                                this.props.setEditTableIdx(-1);
                                this.props.updateDocument(this.props.document._id, {tables}, true);
                            }
                        })
                    
                    // Just delete the column 
                    const columnIdx = this.state.contextMenuCell.column;
                    table.rows.forEach((row) => {
                        row.cells.splice(columnIdx, 1);
                    });
                    if (table.headers)
                        table.headers.splice(columnIdx, 1)
                        
                    this.setState({table, contextMenuCell: {}});
                    this.props.saveTableChanges(table);
                }
            },
            {
                label: 'Add column left',
                action: () => {
                    splitCellColumn(this.state.contextMenuCell.column);
                    this.setState({contextMenuCell: {}});
                }
            },
            {
                label: 'Add Column right',
                action: () => {
                    splitCellColumn(this.state.contextMenuCell.column, true);
                    this.setState({contextMenuCell: {}});
                }
            },
            {
                label: 'Drag-select new column',
                action: () => {
                    this.props.setFunction('draw-input-bounds', {
                        callback: (page, x, y, width, height) => {
                            // fit the column y and height to the table bounds 
                            const table = this.state.table; 

                            // for each row, create the new cell 
                            table.rows.forEach((row) => {
                                row.cells.push({
                                    x,
                                    y: row.y,
                                    width,
                                    height: row.height,
                                    text: ''
                                });

                                // update the row bounds (x, width) 
                                row.x = Math.min(row.x, x);
                                // sort the cells by x
                                row.cells.sort((a,b) => {return a.x - b.x});
                                row.width = row.cells[row.cells.length - 1].x + row.cells[row.cells.length - 1].width - row.x;
                            });
                            
                            // find the index of the new column and add a header 
                            const columnIdx = table.rows[0].cells.findIndex((cell) => cell.x === x);
                            table.headers.splice(columnIdx, 0, '');

                            this.setState({table, contextMenuCell: {}});
                            this.props.saveTableChanges(this.state.table);

                            this.props.setFunction();    
                        }
                    });
                }
            },
            {
                label: 'Tag cell',
                action: () => {
                    const table = this.state.table; 
                    table.tags = [...table.tags??[], {
                        row: this.state.contextMenuCell.row, 
                        col: this.state.contextMenuCell.column,
                        key: ''
                    }];

                    this.setState({table, contextMenuCell: {}});
                    this.props.saveTableChanges(table); 
                }
            },
            {
                label: 'Tag table',
                action: () => {
                    const table = this.state.table; 
                    table.tags = [...table.tags??[], {
                        row: -1, 
                        col: -1,
                        key: ''
                    }];
                    
                    this.setState({table, contextMenuCell: {}});
                    this.props.saveTableChanges(table); 
                }
            },
            {
                label: 'Populate cell',
                action: () => {
                    const table = this.state.table; 
                    const row = table.rows[this.state.contextMenuCell.row];
                    const cell = table.rows[this.state.contextMenuCell.row].cells[this.state.contextMenuCell.column];
                    cell.text = this.fillCellValue(cell, row);
                    this.setState({table, contextMenuCell: {}});
                    this.props.saveTableChanges(table);
                }
            },
            {
                label: 'Populate page',
                action: () => {
                    const table = this.state.table; 
                    const row = table.rows[this.state.contextMenuCell.row];
                    const page = row.page;  

                    table.rows.forEach((row) => {
                        if(row.page !== page)
                            return; 

                        row.cells.forEach((cell) => {
                            cell.text = this.fillCellValue(cell, row);
                        })
                    });

                    this.setState({table, contextMenuCell: {}});
                    this.props.saveTableChanges(table);
                }
            },
            {
                label: 'Populate empty cells',
                action: () => { this.populateEmptyCells(); },
                shortcut: 'Ctrl + P'
            },
            {
                label: 'Populate table',
                action: () => {
                    const table = this.state.table;
                    table.rows.forEach((row) => {
                        row.cells.forEach((cell) => {
                            cell.text = this.fillCellValue(cell, row);
                        })
                    })
                    this.setState({table, contextMenuCell: {}});
                    this.props.saveTableChanges(table);
                }
            },
        ];
    }

    setContextMenuCell = (cell) => {
        this.setState({ contextMenuCell: cell });
    }

    gotoMatch = (index) => {
        if (this.state.dynamicLoad)
            this.goToMatchDynamic(index);
        else
            this.goToMatchStatic(index);
    }

    // Go to match with dynamic loading
    goToMatchDynamic = (index) => {
        const matches = this.props.matches ? this.props.matches : {
            count : 0,
            index: 0,
            words: []
        };

        let q = this.props.query;
        if (!q || q.length < 3)
            return;

        if (matches.words.count === 0 || 
            typeof matches.words[index].page !== "number")
            return;

        let idx = index * q.trim().split(" ").length;

        // Show relevant pages
        let page = matches.words[idx].page;
        let wordPos = matches.words[idx].y;
        let docLen = this.props.document.pages.length;
        let endIdx = Math.min(docLen-1, Math.ceil(page + RENDER_COUNT));
        let startIdx = Math.max(0, Math.floor(page - RENDER_COUNT));

        this.setState({
            startIdx,
            endIdx
        }, ()=> {

            // Scroll to page
            let sHeight = this.refs.documentViewerScroll.getScrollHeight();
            let sPos = 0;
            let wordOffset;

            // If we have aspect ratios, compute sPos using that
            const doc = this.props.document;
            if (doc.aspects && Object.keys(doc.aspects).length === docLen) {
                const pWidth = this.state.zoom * (window.innerHeight * 0.78 - 60);

                for (let pageIdx=0; pageIdx<page; pageIdx++) {
                    
                    // Update aspects using rotation
                    let AR = doc.aspects[String(pageIdx)];
                    if (doc.rotations && [90, -90, 270].includes(doc.rotations[pageIdx]))
                        AR = 1/AR;

                    // Increment scroll position by page height
                    sPos += (pWidth * AR + PAGE_MARGIN);
                }
                    
                // Update aspects using rotation
                let AR = doc.aspects[String(page)];
                if (doc.rotations && [90, -90, 270].includes(doc.rotations[page]))
                    AR = 1/AR;

                wordOffset = wordPos * (pWidth * AR);
            } else {
                sPos = sHeight * page / docLen;
                wordOffset = wordPos * (sHeight/docLen);
            }

            // Scroll to word
            let scrollTo = sPos + wordOffset - 200;

            this.refs.documentViewerScroll.view.scroll({
                top: scrollTo,
                behavior: 'smooth'
            });

            // Let parent know
            this.props.gotoMatch(index);
        });  
    }

    // Go to match statically
    goToMatchStatic = (index) => {
        // Scroll to div
        const mref = document.getElementById("document-match-" + index);
        if (mref) {
            const match = mref.getBoundingClientRect();
            this.refs.documentViewerScroll.view.scroll({
                top: this.refs.documentViewerScroll.getScrollTop() + match.top - 300,
                behavior: 'smooth'
            });
        }

        // Let parent know
        this.props.gotoMatch(index);
    }

    // Add comment
    addComment = async (page, clickX, clickY) => {
        if (!this.props.document || !this.props.user) return; 
        await this.props.loading(9999);

        // Get x and y coordinate for comment
        const pageRect = document.getElementById("page-" + String(page)).getBoundingClientRect();
        const x = (clickX - pageRect.x) / pageRect.width;
        const y = (clickY - pageRect.y) / pageRect.height;

        const res = await api.post(this.url + '/comment', {
            docId: this.props.document._id,
            comment: {
                page,
                thread: [],
                x,
                y
            }
        }, true, {cancelToken: this.cancelToken.token});

        if (res && res.success && res.notes) {
            await this.props.updateDocument({
                notes: res.notes
            }, false);
            await this.setState({
                newComment: res.notes[res.notes.length-1]._id
            });
        }

        else toast.error("Unable to create new comment!");

        this.props.loaded();
    }

    // Reply to comment
    replyToComment = async (commentId, text, tags=[]) => {
        const doc = this.props.document;
        if (!doc || !doc.notes || !this.props.user) return; 

        this.setState({ newComment: null });
        let notes = doc.notes.slice();
        const noteIdx = notes.findIndex(n => n._id === commentId);
        if (noteIdx < 0 || !notes[noteIdx].thread) return;
        
        notes[noteIdx].thread.push({
            text,
            user: this.props.user._id
        });

        // Any new tags?
        if (!notes[noteIdx].tags) {
            notes[noteIdx].tags = tags;
        } else {
            for (const tag of tags)
                if (!notes[noteIdx].tags.includes(tag))
                    notes[noteIdx].tags.push(tag);
        }

        await this.props.updateDocument({ notes });

        // Notify users!
        for (const userId of tags)
            this.props.notify(userId, {
                title: "You have been tagged!",
                body: this.props.user.name + " has tagged you in a comment on the document: <strong>" + doc.name + "</strong>:<br />" + text + "<br /><br /><a href=\"" + api.publicUrl + "/stack/" + doc.stack + "/document/" + doc._id + "?comment=" + commentId + "\">Click here</a> to view."
            });
    }

    // Delete a comment
    deleteComment = async (commentId) => {
        if (!this.props.document || !this.props.document.notes || !this.props.user) return; 
        this.setState({ newComment: null });
        let notes = this.props.document.notes.slice();
        const noteIdx = notes.findIndex(n => n._id === commentId);
        if (noteIdx < 0) return;
        notes.splice(noteIdx, 1);
        await this.props.updateDocument({ notes });
    }

    range = (start, end) => {
        return Array(end - start + 1).fill().map((_, idx) => start + idx);
    }

    updateTable = (table) => {
        if((!this.state.statementConfirmed && this.props.user.email.endsWith('@stax.ai'))||(!this.state.statementClientConfirmed && !this.props.user.email.endsWith('@stax.ai'))) this.setState({table});
        else toast.error("This statement is approved. You cannot update transactions on this statement.");    
    }

    render = () => {

        // const user = this.props.user;
        const startIdx = this.state.startIdx;
        const endIdx = this.state.endIdx;
        const doc = this.props.document ? this.props.document : { _id: null, pages: [], canvas: {}, notes: [], words: [] };
        const renderedPages = this.range(startIdx, endIdx);

        const matches = this.props.matches ? this.props.matches : {
            count : 0,
            index: 0,
            words: []
        };
        
        const docLen = doc.pages.length;

        // Find sizes of blank space
        var topHeight = 0;
        var botHeight = 0;

        // If the css is ever updated, this will need to be updated as well. Sorry.
        for (let i=0; i<docLen; i++) {
            let pageHeight = 0;

            // Update aspects using rotation
            let AR = (this.state.aspects && i < this.state.aspects.length) ? this.state.aspects[String(i)] : ASPECT_RATIO;
            if (doc.rotations && [90, -90, 270].includes(doc.rotations[i]))
                AR = 1/AR;

            pageHeight = (this.state.zoom * (window.innerHeight * 0.78 - 60)) * AR + PAGE_MARGIN

            if (i < startIdx)
                topHeight += pageHeight;
            else if (i > endIdx)
                botHeight += pageHeight;
        }

        return (<div
            className={"document-modal"  + (this.props.hideMeta ? " document-modal-nometa" : "")}
            ref="document"
            // onClick={this.backToStack}
            onMouseMove={this.showToolbar}
        >
            <div className="document-modal-shadow" />

            <img
                className="document-close"
                src="https://static.stax.ai/images/icons/close.png"
                alt="x"
                title="Back to stack"
                onClick={this.backToStack}
            />

            <Scrollbars
                renderTrackVertical={props => <div className="scrollbar-track-v" {...props} />}
                renderTrackHorizontal={props => <div className="scrollbar-track-h" {...props} />}
                renderThumbVertical={props => <div className="scrollbar-thumb-v" {...props} />}
                renderThumbHorizontal={props => <div className="scrollbar-thumb-h" {...props} />}
                ref="documentViewerScroll" onScroll={this.onDocScroll}
            >

                {topHeight > 0 && <div
                    ref={this.topBlank}
                    className="blankPage"
                    style={{
                        width: "calc(" + this.state.zoom + " * (78vh - 60px))",
                        height: topHeight + "px",
                    }}
                />}

                {renderedPages.map(page => <Page
                    onContextMenu={(e) => this.openPageMenu(e,page)}
                    ctrl={this.state.ctrlDown}
                    key={page}
                    pageIdx={page}
                    pageKey={page}
                    documentName={doc.name}
                    documentId={doc._id}
                    aspectRatio={doc.aspects && doc.aspects[String(page)]}
                    pageData={this.state.pages[page]}
                    rotation={doc.rotations ? doc.rotations[page] : 0}
                    user={this.props.user}
                    users={this.props.team && this.props.team.users ? this.props.team.users : []}
                    words={doc.words ? doc.words.filter(w => w.page === page) : []}
                    bounds={doc.bounds ? doc.bounds : []}
                    setBounds={(bounds) => this.props.updateDocument({ bounds })}
                    inputs={doc.fillable && doc.fillable.enabled && doc.fillable.fields ? doc.fillable.fields.map((f,idx) => ({ ...f, idx })).filter(f => f.page === page) : []}
                    split={doc.split ? doc.split : []}
                    setSplit={split => this.props.updateDocument({ split })}
                    zoom={this.state.zoom}
                    hovered={this.props.hovered}
                    loading={this.props.loading}
                    loaded={this.props.loaded}
                    table={this.props.editTableIdx >= 0? doc.tables[this.props.editTableIdx] :{}}
                    editTableIdx={this.props.editTableIdx}
                    saveTableChanges={this.props.saveTableChanges}
                    setContextMenuOptions={this.setContextMenuOptions}
                    // Canvas
                    tool={
                        this.props.function === "draw-input-bounds" ? "InsertInput" :
                        // this.props.function === "retag-field" ? "TagField" :
                        this.state.activeTool
                    }
                    setFunction={this.props.setFunction}
                    function={this.props.function}
                    functionData={this.props.functionData}  
                    callback={this.props.functionData && this.props.functionData.callback ? this.props.functionData.callback : () => {}}
                    color={this.state.toolColor}
                    setTool={tool => this.setState({ activeTool: tool })}
                    canvas={doc.canvas ? doc.canvas[page] : ""}
                    setCanvas={canvas => this.setState({
                        canvas: {
                            ...this.state.canvas,
                            [page]: canvas
                        }
                    })}
                    saveCanvas={() => this.saveCanvas(page)}

                    // Text selection
                    selection={this.state.selection.filter(w => w ? w.page === page : false)}
                    select={selection => this.setState({ selection })}

                    // Search and matches
                    query={this.props.query}
                    matches={matches.words.filter(m => m.page === page)}
                    matchIdx={matches.index}

                    // Comments
                    comments={doc && doc.notes ? doc.notes.filter(n => n.page === page): []}
                    newCommentId={this.state.newComment}
                    addComment={(x,y,text) => this.savereply(page,x,y,text)}
                    replyToComment={this.replyToComment}
                    deleteComment={this.deleteComment}
                />)}

                {
                    this.props.editTableIdx >= 0? 
                    <TableEditor 
                        table={this.state.table}
                        updateTable={this.updateTable}
                        contextMenuOptions={this.getTableContextMenuOptions}
                        onContextMenu={(e) => this.openTableMenu(e)}
                        closeContextMenu={(e)=>this.closeMenu(e)}
                        setContextMenuCell={this.setContextMenuCell}
                        contextMenuCell={this.state.contextMenuCell}
                        zoom={this.state.zoom} 
                        {...this.props}
                    />:""
                }

                {botHeight > 0 && <div
                    ref={this.bottomBlank}
                    className="blankPage"
                    style={{
                        width: "calc(" + this.state.zoom + " * (78vh - 60px))",
                        height: botHeight + "px",
                    }}
                />}

            </Scrollbars>

            <div
                className={"document-toolbar" + (this.state.showToolbar ? " document-toolbar-show" : "")}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                {this.renderToolbar()}

                {this.state.colorPicker && <div className="document-toolbar-color-picker">
                    {colors.map((c,idx) => <div
                        key={idx}
                        className={"document-toolbar-color-option" + (this.state.toolColor === c ? " document-toolbar-color-option-active" : "")}
                        style={{
                            backgroundColor: c
                        }}
                        onClick={() => this.setState({ toolColor: c, colorPicker: false })}
                    />)}
                </div>}

            </div>

            {/* Save status message */}
            {this.state.saveStatus !== "" && <div className="document-save-status">
                {this.state.saveStatus}
            </div>}

            {/* Help icon */}
            <img
                className="document-help"
                src="https://static.stax.ai/images/icons/info.png"
                alt="(i)"
                title="Show help screen"
                style={matches.count > 0 ? {
                    top: 80
                } : {}}
                onClick={() => this.props.openNew}
            />

            {/* Search result/match counter */}
            {matches.count > 0 && <MatchCounter
                className={this.props.hideMeta ? "match-counter-hidemeta" : ""}
                idx={this.props.matches.index}
                count={this.props.matches.count}
                goto={this.gotoMatch}
            />}

            {/* Document page counter */}
            <div
                className="document-page-counter"
                style={{ opacity: this.state.showPageCounter ? 1 : 0 }}
            >Page {this.state.currentPage+1} of {doc.pages.length}</div>
            
            
            <ContextMenu
                load={(open,close) => {
                    this.openPageMenu= open;
                    this.closeMenu = close;
                }}
                options={this.getPageContextMenuOptions()}
            />

            
            <Foco
                onClickOutside={()=>{
                    this.closeMenu()
                    this.setState({
                        contextMenuCell: {}
                    })
                }}
            >
                <ContextMenu
                    load={(open,close) => {
                        this.openTableMenu = open;
                        this.closeMenu = close;
                    }}
                    options={this.getTableContextMenuOptions()}
                />
            </Foco>
        </div>);

    }

};